import * as React from "react";
import { Check, ChevronsUpDown, type LucideIcon } from "lucide-react";
import { Button } from "../ui/button.js";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu.js";
import { cn } from "../../lib/cn.js";

export interface ScopeOption {
  id: string;
  name: string;
}

export interface ScopeSelectorProps {
  label: string;
  icon: LucideIcon;
  options: ScopeOption[];
  value: string | null;
  onChange: (id: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Header context selector (school/campus/academic-year), per `07_UI_UX_SPEC.md` §3 and
 * `17_UI_VISUAL_DESIGN_SYSTEM.md` §2.1: "Context selectors must not dominate the screen" and
 * "if only one option exists, selection may be compact." Never expands backend authorization —
 * it only changes which already-authorized data the caller requests.
 */
export function ScopeSelector({ label, icon: Icon, options, value, onChange, placeholder = "Select…", className }: ScopeSelectorProps) {
  const selected = options.find((option) => option.id === value);

  if (options.length === 0) {
    return null;
  }

  if (options.length === 1 && options[0]) {
    return (
      <div className={cn("flex items-center gap-1.5 rounded-[var(--radius-control)] px-2 py-1.5 text-sm text-muted-foreground", className)}>
        <Icon className="size-3.5 shrink-0" />
        <span className="max-w-[10rem] truncate font-medium text-foreground">{options[0].name}</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={cn("max-w-[12rem] justify-between gap-1.5 font-normal", className)}>
          <span className="flex min-w-0 items-center gap-1.5">
            <Icon className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">{selected ? selected.name : placeholder}</span>
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((option) => (
          <DropdownMenuItem key={option.id} onSelect={() => onChange(option.id)}>
            <Check className={cn("size-4", option.id !== value && "opacity-0")} />
            <span className="truncate">{option.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
